import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";

// Gated App Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import JobsPage from "./pages/JobsPage";
import JobDetailPage from "./pages/JobDetailPage";
import CustomersPage from "./pages/CustomersPage";
import TimeTrackingPage from "./pages/TimeTrackingPage";
import TimerPage from "./pages/TimerPage";
import ExpensesPage from "./pages/ExpensesPage";
import InvoicesPage from "./pages/InvoicesPage";
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import Layout from "./components/Layout";

// Public Website Pages
import PublicHomePage from "./pages/public/PublicHomePage";
import AboutPage from "./pages/public/AboutPage";
import ServicesPage from "./pages/public/ServicesPage";
import PortfolioPage from "./pages/public/PortfolioPage";
import PortfolioDetailPage from "./pages/public/PortfolioDetailPage";
import BlogPage from "./pages/public/BlogPage";
import BlogPostPage from "./pages/public/BlogPostPage";
import ContactPage from "./pages/public/ContactPage";
import PrivacyPolicyPage from "./pages/public/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/public/CookiePolicyPage";
import TermsOfServicePage from "./pages/public/TermsOfServicePage";

// Admin CMS Pages
import BlogManagementPage from "./pages/admin/BlogManagementPage";
import PortfolioManagementPage from "./pages/admin/PortfolioManagementPage";
import ContactSubmissionsPage from "./pages/admin/ContactSubmissionsPage";
import SiteContentPage from "./pages/admin/SiteContentPage";
import StyleGuidePage from "./pages/admin/StyleGuidePage";
import DatabaseBackupPage from "./pages/DatabaseBackupPage";
import DataExportPage from "./pages/DataExportPage";
import StoragePage from "./pages/StoragePage";
import SweetShopLanding from "./pages/SweetShopLanding";
import SweetShopWithCart from "./pages/SweetShopWithCart";

// Style Provider
import { StyleProvider } from "./contexts/StyleProvider";
import { TimerProvider } from "./contexts/TimerContext";
import FloatingTimerButton from "./components/FloatingTimerButton";
import CookieConsentBanner from "./components/CookieConsentBanner";
import GoogleAnalytics from "./components/GoogleAnalytics";
import CanonicalURL from "./components/CanonicalURL";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('uh_token'));
  const [loading, setLoading] = useState(true);

  // Axios response interceptor: transparently refresh access token on 401
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const original = error.config || {};
        const refresh = localStorage.getItem('uh_refresh');
        if (
          error.response?.status === 401 &&
          refresh &&
          !original._retry &&
          !original.url?.includes('/auth/refresh') &&
          !original.url?.includes('/auth/login')
        ) {
          original._retry = true;
          try {
            const { data } = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh });
            localStorage.setItem('uh_token', data.token);
            setToken(data.token);
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${data.token}`;
            return axios(original);
          } catch (e) {
            localStorage.removeItem('uh_token');
            localStorage.removeItem('uh_refresh');
            setToken(null);
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (err) {
          localStorage.removeItem('uh_token');
          localStorage.removeItem('uh_refresh');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, refresh_token, user: userData } = response.data;
    localStorage.setItem('uh_token', newToken);
    if (refresh_token) localStorage.setItem('uh_refresh', refresh_token);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('uh_token');
    localStorage.removeItem('uh_refresh');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAdmin: user?.role === 'admin',
    isFinance: user?.role === 'finance' || user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, token } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <TimerProvider>
        <BrowserRouter>
          <ScrollToTop />
          <CanonicalURL />
          <GoogleAnalytics />
          <FloatingTimerButton />
          <CookieConsentBanner />
          <div className="App">
            <Toaster position="top-right" richColors />
            <Routes>
            {/* Public Website Routes - Wrapped with StyleProvider */}
            <Route path="/" element={<StyleProvider><PublicHomePage /></StyleProvider>} />
            <Route path="/about" element={<StyleProvider><AboutPage /></StyleProvider>} />
            <Route path="/services" element={<StyleProvider><ServicesPage /></StyleProvider>} />
            <Route path="/portfolio" element={<StyleProvider><PortfolioPage /></StyleProvider>} />
            <Route path="/portfolio/:slug" element={<StyleProvider><PortfolioDetailPage /></StyleProvider>} />
            <Route path="/blog" element={<StyleProvider><BlogPage /></StyleProvider>} />
            <Route path="/blog/:slug" element={<StyleProvider><BlogPostPage /></StyleProvider>} />
            <Route path="/contact" element={<StyleProvider><ContactPage /></StyleProvider>} />
            <Route path="/privacy-policy" element={<StyleProvider><PrivacyPolicyPage /></StyleProvider>} />
            <Route path="/cookie-policy" element={<StyleProvider><CookiePolicyPage /></StyleProvider>} />
            <Route path="/terms-of-service" element={<StyleProvider><TermsOfServicePage /></StyleProvider>} />
            
            {/* Hidden Sweet Shop Landing (not linked from main site) */}
            <Route path="/sweet-shop" element={<SweetShopLanding />} />
            <Route path="/sweet-shop-cart" element={<SweetShopWithCart />} />
            
            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout><DashboardPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs"
              element={
                <ProtectedRoute>
                  <Layout><JobsPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs/:jobId"
              element={
                <ProtectedRoute>
                  <Layout><JobDetailPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <Layout><CustomersPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/time-tracking"
              element={
                <ProtectedRoute>
                  <Layout><TimeTrackingPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/timer"
              element={
                <ProtectedRoute>
                  <Layout><TimerPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses"
              element={
                <ProtectedRoute>
                  <Layout><ExpensesPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <Layout><InvoicesPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout><UsersPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout><SettingsPage /></Layout>
                </ProtectedRoute>
              }
            />
            {/* Admin CMS Routes */}
            <Route
              path="/admin/blog"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout><BlogManagementPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/portfolio"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout><PortfolioManagementPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/contacts"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout><ContactSubmissionsPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/site-content"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout><SiteContentPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/style-guide"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout><StyleGuidePage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/backups"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout><DatabaseBackupPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/data-export"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout><DataExportPage /></Layout>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/storage"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Layout><StoragePage /></Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          </div>
        </BrowserRouter>
      </TimerProvider>
    </AuthProvider>
  );
}

export default App;
